import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

export interface ParentSummaryMetadata {
  clientName?: string;
  sessionDate?: string;
  sessionTime?: string;
  duration?: string;
  sessionType?: string;
  rbtName?: string;
}

interface GenerateParentSummaryRequest {
  noteData: Record<string, any>;
  template: string;
  sessionMetadata?: ParentSummaryMetadata;
}

interface GenerateParentSummaryResponse {
  success: boolean;
  parentSummary?: string;
  error?: string;
}

/**
 * Calls the generateParentSummary Cloud Function to turn an approved
 * clinical note into a warm, plain-language parent summary. 30s per-call
 * timeout; retries once. The Anthropic key lives only in the function.
 */
class ParentSummaryService {
  async generate(
    noteData: Record<string, any>,
    template: string,
    sessionMetadata?: ParentSummaryMetadata
  ): Promise<string> {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('You must be logged in to generate a parent summary');
    }

    const functions = getFunctions();
    const callable = httpsCallable<GenerateParentSummaryRequest, GenerateParentSummaryResponse>(
      functions,
      'generateParentSummary',
      { timeout: 30000 }
    );

    const attempt = async (): Promise<string> => {
      const res = await callable({ noteData, template, sessionMetadata });
      if (!res.data.success || !res.data.parentSummary) {
        throw new Error(res.data.error || 'Parent summary generation failed');
      }
      return res.data.parentSummary;
    };

    try {
      return await attempt();
    } catch (err) {
      console.error('generateParentSummary failed, retrying once:', err);
      return await attempt(); // retry once
    }
  }
}

export const parentSummaryService = new ParentSummaryService();
